import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import CardLayout from "../../../common/cardLayout/CardLayout";
import Pdf from "react-to-pdf";
import {
  fetchDiagnosisDataLogic,
  singlePatientLogic,
} from "../../../../store/features/patient/patientSlice";
import { getPatientBHIDataLogic } from "../../../../store/features/patient/bhiSlice";
import {
  fetchAllAssessmentsLogic,
  fetchRecentAssessmentsLogic,
} from "../../../../store/features/patient/assessmentsSlice";
import {
  findPrevMonth,
  generateMonthlyObjectsReverse,
  findClinician,
  getFullName,
  reportStatus,
} from "../../../../util/helpers";
import PatientLoader from "../../../common/PatientLoader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PatientIntro from "./PatientIntro";
import PatientInformation, { InformationBox } from "./PatientInformation";
import PatientDiagnosisTable from "./PatientDiagnosisTable";
import SidebarLayout from "../../../common/sidebar/SidebarLayout";
import { isEmptyObject } from "jquery";
import { CheckInOverviewTable } from "../../patient/InfoTabs/CheckInOverview/CheckInOverview";
import PatientAssessmentsOverview from "./PatientAssessmentsOverview";
import PatientConsentData from "./PatientConsentData";

const SubHeading = ({ text }) => {
  return (
    <div className="d-flex w-100 container-fluid">
      <h5 className="primary-text mb-2">{text}</h5>
    </div>
  );
};

const BillingReport = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const options = generateMonthlyObjectsReverse();
  const pdfTarget = useRef();
  const downloadBtn = useRef();

  const search = useLocation().search;
  let shouldDownload = new URLSearchParams(search).get("download");
  shouldDownload = Boolean(shouldDownload);

  const [patientId, setPatientId] = useState(null);
  const [noTakenAssessments, setNoTakenAssessments] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [clinician, setClinician] = useState({});
  const [endDate, setEndDate] = useState(options[0]);
  const [isBHIEligible, setIsBHIEligible] = useState(false);
  const [isCompleted, setCompleted] = useState(false);

  const { loading, singlePatientData, diagnosisData, bhiPatient } = useSelector(
    (state) => state.patient
  );
  const { assessmentsLoading, recentAssessments, assessmentsData } =
    useSelector((state) => state.assessment);
  const { bhiLoading, bhiData } = useSelector((state) => state.bhi);
  const { staffDataLoading, staffData } = useSelector((state) => state.app);

  useEffect(() => {
    if (!assessmentsLoading && !isEmptyObject(recentAssessments)) {
      let counter = 0;
      Object.values(recentAssessments).forEach((item) => {
        if (item.length > 0) {
          counter += 1;
        }
      });
      setNoTakenAssessments(counter > 0);
    }
  }, [assessmentsLoading, recentAssessments]);

  function bundlePatientAPIs(id) {
    if (!id) {
      return;
    }
    dispatch(singlePatientLogic({ id: id }));
    dispatch(getPatientBHIDataLogic(id));
    dispatch(fetchRecentAssessmentsLogic(id));
    dispatch(fetchDiagnosisDataLogic({ patient: id, ordering: "-created" }));
    dispatch(
      fetchAllAssessmentsLogic({
        patient_id: id.toString(),
        start_date: findPrevMonth(endDate),
        end_date: endDate,
      })
    );
  }
  useEffect(() => {
    setPatientId(parseInt(params?.patientid));
    if (params?.date) {
      setEndDate(params?.date);
    }
  }, []);

  useEffect(() => {
    if (patientId) {
      bundlePatientAPIs(patientId);
    }
  }, [patientId]);

  useEffect(() => {
    if (bhiLoading || loading || assessmentsLoading || staffDataLoading) {
      setLocalLoading(true);
    }

    if (!bhiLoading && !loading && !assessmentsLoading && !staffDataLoading) {
      setLocalLoading(false);
    }
  }, [bhiLoading, loading, assessmentsLoading, staffDataLoading]);

  useEffect(() => {
    if (!staffDataLoading) {
      const targetStaff = findClinician(
        staffData,
        singlePatientData?.clinician
      );
      setClinician(targetStaff);
    }
  }, [staffDataLoading]);

  useEffect(() => {
    if (
      !assessmentsLoading &&
      assessmentsData &&
      singlePatientData?.bhi_eligible
    ) {
      setIsBHIEligible(true);
    } else {
      setIsBHIEligible(false);
    }
  }, [assessmentsLoading]);

  function closeWindowAfterDownload() {
    setTimeout(() => {
      window.close();
      window.clearTimeout();
    }, 250);
  }
  useEffect(() => {
    if (!localLoading) {
      setTimeout(() => {
        if (shouldDownload) {
          downloadBtn.current.click();
          window.clearTimeout();
        }
      }, 2500);
    }
  }, [localLoading]);

  const pdfOptions = {
    orientation: "portrait",
    unit: "in",
    format: [30, 17],
  };

  return (
    <SidebarLayout>
      <div className="container-fluid">
        <div className="d-flex justify-content-between container-fluid w-100 my-5 px-1">
          <div className="d-flex" style={{ gap: "15px" }}>
            <h4
              className="primary-text-blue"
              title="Back to dashboard"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/billing/")}
            >
              {" "}
              {"<"}{" "}
            </h4>
            <h4 className="primary-text-blue">
              Monthly BHI Report - {moment(endDate).format("MMM, YYYY")}
            </h4>
          </div>
          <Pdf
            targetRef={pdfTarget}
            filename={`${moment(new Date()).format(
              "MM-DD-YYYY--HH_mm_ss"
            )}-${getFullName(singlePatientData)}`}
            options={pdfOptions}
            onComplete={() => {
              setCompleted(true);
              closeWindowAfterDownload();
            }}
          >
            {({ toPdf }) => (
              <button
                ref={downloadBtn}
                onClick={() => {
                  if (!isCompleted) {
                    toPdf();
                  }
                }}
                className="primary-btn-small"
              >
                Download Report
              </button>
            )}
          </Pdf>
        </div>
        <div className="container-fluid w-100" ref={pdfTarget}>
          {shouldDownload && (
            <div className="text-center container-fluid w-100">
              <h4 className="primary-text-blue">
                Monthly BHI Report - {moment(endDate).format("MMM, YYYY")}
              </h4>
              <br />
            </div>
          )}

          <CardLayout className="px-2 my-2">
            {localLoading ? (
              <PatientLoader />
            ) : (
              <div className="row container-fluid w-100">
                <div className="col-sm-12 col-md-6 col-lg-6">
                  <PatientIntro
                    downloadMode={shouldDownload}
                    singlePatientData={singlePatientData}
                    loading={loading}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                  <div
                    className="d-flex my-5 justify-content-end"
                    style={{ gap: "50px" }}
                  >
                    <InformationBox
                      labelText={"Review Date:"}
                      type={"strong"}
                      information={moment(bhiData?.modified).format(
                        "MM/DD/YYYY (hh:mm A)"
                      )}
                    />
                    <InformationBox
                      labelText={"Treating Provider:"}
                      type={"strong"}
                      information={getFullName(clinician)}
                    />
                  </div>
                </div>

                <div
                  className="d-flex container-fluid w-100"
                  style={{ gap: "5px" }}
                >
                  <PatientInformation
                    singlePatientData={singlePatientData}
                    bhiData={bhiData}
                    bhiLoading={bhiLoading}
                    loading={loading}
                    isBHIEligible={isBHIEligible}
                  />
                </div>
              </div>
            )}
          </CardLayout>

          <CardLayout className="p-3 container-fluid w-100">
            <div className="w-100 container-fluid d-flex my-2">
              <h4 className="primary-text-blue">Behavioral Health Summary</h4>
            </div>

            <PatientAssessmentsOverview
              recentAssessments={recentAssessments}
              noTakenAssessments={noTakenAssessments}
            />

            <div className="container-fluid w-100">
              <SubHeading text={"Update Activity History"} />
              <PatientDiagnosisTable
                diagnosisData={diagnosisData}
                clinician={clinician}
                staffData={staffData}
              />
            </div>

            <div className="container-fluid w-100">
              <SubHeading text={"Patient Engagement Activity"} />
              <div className="container-fluid w-100 d-flex justify-content-center text-center">
                <CheckInOverviewTable showHeading={false} />
              </div>
            </div>
          </CardLayout>

          <CardLayout className="p-3 container-fluid my-4">
            <div className="w-100 container-fluid d-flex my-2">
              <h4 className="primary-text-blue">BHI Program Documentation</h4>
            </div>

            {localLoading ? (
              <PatientLoader />
            ) : (
              <>
                {bhiData?.consent_information && (
                  <PatientConsentData
                    bhiData={bhiData}
                    singlePatientData={singlePatientData}
                  />
                )}
              </>
            )}
          </CardLayout>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default BillingReport;
